/**
 * Product page behavior for eyewear.
 *
 * - Resolves the selected variant from the option radios.
 * - Updates price, add-to-cart state, URL and the price difference shown
 *   next to each lens option.
 * - Brings the selected color's photo to the front of the gallery. When
 *   photos are tagged with a color in their alt text (for example "#C1" or
 *   the full color name), only that color's photos are shown.
 * - Shows a sticky buy bar while the main add-to-cart button is off screen.
 */
if (!customElements.get('product-info')) {
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
      this.optionGroups = Array.from(this.querySelectorAll('[data-option]'));
      this.gallery = this.querySelector('[data-gallery-list]');

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-input]')) this.onOptionChange();
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-prescription-method]')) this.updatePrescriptionPanels();
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-plan-input]')) this.updatePrice(this.currentVariant());
      });

      this.setUpGallery();

      this.setUpRecipient();
      this.setUpModels();
      if (this.selectedPlan()) this.updatePrice(this.currentVariant());
      this.setUpStickyBuy();
      this.applyColorPhotos(this.currentVariant());
      this.updatePrescription(this.currentVariant());
    }

    selectedValues() {
      return this.optionGroups.map((group) => {
        const checked = group.querySelector('[data-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    findVariant(values) {
      return this.variants.find((variant) => variant.options.every((option, index) => option === values[index]));
    }

    currentVariant() {
      if (!this.optionGroups.length) return this.variants[0];
      return this.findVariant(this.selectedValues());
    }

    onOptionChange() {
      const values = this.selectedValues();
      const variant = this.findVariant(values);

      this.updateOptionStates(values, variant);
      this.updateBuy(variant);
      this.updateRestock(variant);
      this.updatePrescription(variant);
      if (!variant) return;

      this.updatePrice(variant);
      this.applyColorPhotos(variant);
      this.updateUrl(variant);
    }

    updateOptionStates(values, variant) {
      this.optionGroups.forEach((group, index) => {
        const selected = group.querySelector('[data-option-selected]');
        if (selected) selected.textContent = values[index] || '';

        // METRONOME-Tokyo Online: the price difference is counted from the
        // cheapest choice of the option ("なし"), not from the choice selected
        // now, so the amounts stay the same whichever one is picked.
        const candidates = [...group.querySelectorAll('[data-option-input]')].map((input) => {
          const candidateValues = values.slice();
          candidateValues[index] = input.value;
          return { input, candidate: this.findVariant(candidateValues) };
        });
        const prices = candidates.filter((entry) => entry.candidate).map((entry) => entry.candidate.price);
        const base = prices.length ? Math.min(...prices) : 0;
        // With a single option the choice is the whole price (the repair menu),
        // so the price itself is shown instead of a difference.
        const whole = values.length === 1 && prices.some((price) => price > base);

        candidates.forEach(({ input, candidate }) => {
          const wrapper = input.closest('.option__value');

          wrapper.classList.toggle('is-unavailable', !candidate || !candidate.available);

          const delta = wrapper.querySelector('[data-option-delta]');
          if (!delta) return;

          if (candidate && whole) {
            delta.textContent = this.formatMoney(candidate.price);
          } else if (!candidate || candidate.price <= base) {
            delta.textContent = '';
          } else {
            delta.textContent = '+' + this.formatMoney(candidate.price - base);
          }
        });
      });
    }

    updateBuy(variant) {
      const idInput = this.querySelector('[data-variant-id]');
      if (idInput && variant) idInput.value = variant.id;

      this.querySelectorAll('[data-add-button]').forEach((button) => {
        const label = button.querySelector('[data-add-label]');
        if (!variant) {
          button.disabled = true;
          label.textContent = this.strings.unavailable;
        } else if (!variant.available) {
          button.disabled = true;
          label.textContent = this.strings.soldOut;
        } else {
          button.disabled = false;
          label.textContent = this.strings.addToCart;
        }
      });
    }

    /* Restock request: shown only while the selected variant is sold out. */

    updateRestock(variant) {
      const restock = this.querySelector('[data-restock]');
      if (!restock) return;
      restock.hidden = !variant || variant.available;
      const field = restock.querySelector('[data-restock-product]');
      if (field && variant) field.value = field.dataset.productTitle + ' / ' + variant.title;
    }

    /*
     * Prescription: the block appears only when the chosen options include
     * one of its trigger words (for example a prescription lens). Hidden
     * fields are disabled so they are not sent with the order.
     */

    updatePrescription(variant) {
      const block = this.querySelector('[data-prescription]');
      if (!block) return;
      const triggers = (block.dataset.triggers || '')
        .split(',')
        .map((word) => word.trim())
        .filter(Boolean);
      const title = variant ? variant.options.join(' / ') : '';
      const needed = triggers.length === 0 || triggers.some((word) => title.includes(word));

      block.hidden = !needed;
      block.querySelectorAll('[data-prescription-method]').forEach((input) => {
        input.disabled = !needed;
      });
      this.updatePrescriptionPanels();
    }

    updatePrescriptionPanels() {
      const block = this.querySelector('[data-prescription]');
      if (!block) return;
      const checked = block.querySelector('[data-prescription-method]:checked:not(:disabled)');
      const active = checked ? checked.dataset.prescriptionMethod : null;

      block.querySelectorAll('[data-prescription-panel]').forEach((panel) => {
        const show = !block.hidden && panel.dataset.prescriptionPanel === active;
        panel.hidden = !show;
        panel.querySelectorAll('input').forEach((input) => {
          input.disabled = !show;
        });
      });
    }

    /* The subscription plan chosen in the form, or null for a one-time purchase. */

    selectedPlan() {
      const checked = this.querySelector('[data-plan-input]:checked');
      return checked && checked.value ? Number(checked.value) : null;
    }

    updatePrice(variant) {
      if (!variant) return;
      const plan = this.selectedPlan();
      const allocation = plan
        ? (variant.selling_plan_allocations || []).find((item) => item.selling_plan_id === plan)
        : null;
      const price = allocation ? allocation.price : variant.price;
      const comparePrice = allocation ? allocation.compare_at_price : variant.compare_at_price;

      this.querySelectorAll('[data-price]').forEach((element) => {
        element.textContent = this.formatMoney(price);
      });

      const compare = this.querySelector('[data-compare-price]');
      if (compare) {
        const onSale = comparePrice && comparePrice > price;
        compare.hidden = !onSale;
        if (onSale) compare.textContent = this.formatMoney(comparePrice);
      }

      this.updateUnitPrice(variant);
      this.updatePickup(variant);
    }

    updateUnitPrice(variant) {
      const holder = this.querySelector('[data-unit-price]');
      if (!holder) return;
      const measure = variant.unit_price_measurement;
      holder.hidden = !measure;
      if (!measure) return;
      const amount = measure.reference_value !== 1 ? measure.reference_value : '';
      holder.textContent = `${this.formatMoney(variant.unit_price)} / ${amount}${measure.reference_unit}`;
    }

    /* Pickup availability is asked from the store again for the new variant. */

    async updatePickup(variant) {
      const block = this.querySelector('[data-pickup]');
      if (!block) return;
      const content = block.querySelector('[data-pickup-content]');
      try {
        const root = block.dataset.rootUrl.replace(/\/$/, '');
        const response = await fetch(`${root}/variants/${variant.id}/?section_id=pickup-availability`);
        if (!response.ok) throw new Error(String(response.status));
        const fresh = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-pickup-content]');
        content.innerHTML = fresh ? fresh.innerHTML : '';
      } catch (error) {
        content.innerHTML = '';
      }
    }

    /* 3D models: hands the models to Shopify's "view in your space" feature. */

    setUpModels() {
      const data = this.querySelector('[data-models]');
      if (!data || !window.Shopify || !window.Shopify.loadFeatures) return;
      const models = JSON.parse(data.textContent);
      window.Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: (errors) => {
            if (errors) return;
            const start = () => {
              window.ShopifyXR.addModels(models);
              window.ShopifyXR.setupXRElements();
            };
            if (window.ShopifyXR) start();
            else document.addEventListener('shopify_xr_initialized', start, { once: true });
          },
        },
      ]);
    }

    /* Gift card: the recipient fields are sent only when the box is ticked. */

    setUpRecipient() {
      const box = this.querySelector('[data-recipient]');
      if (!box) return;
      const toggle = box.querySelector('[data-recipient-toggle]');
      const fields = box.querySelector('[data-recipient-fields]');
      const offset = box.querySelector('[data-recipient-offset]');
      if (offset) offset.value = String(new Date().getTimezoneOffset());
      const apply = () => {
        fields.hidden = !toggle.checked;
        fields.querySelectorAll('input, textarea').forEach((field) => {
          field.disabled = !toggle.checked;
        });
      };
      toggle.addEventListener('change', apply);
      apply();
    }

    updateUrl(variant) {
      if (!this.dataset.url || !window.history.replaceState) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        /* The address bar can't be updated in some previews; selection still works. */
      }
    }

    /* Gallery */

    setUpGallery() {
      if (!this.gallery) return;
      this.setUpGalleryLimit();
      this.setUpStickyInfo();
      const current = this.querySelector('[data-gallery-current]');
      if (!current) return;

      let frame = null;
      this.gallery.addEventListener('scroll', () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          const items = this.visibleGalleryItems();
          const center = this.gallery.scrollLeft + this.gallery.clientWidth / 2;
          const index = items.findIndex((item) => item.offsetLeft + item.offsetWidth > center);
          current.textContent = String((index === -1 ? items.length - 1 : index) + 1);
        });
      });
    }

    /*
     * On computers only the first few photos are laid out; a button opens
     * the rest. Phones swipe through every photo, so nothing is held back.
     */
    setUpGalleryLimit() {
      this.galleryLimit = Number(this.gallery.dataset.galleryLimit) || 0;
      this.galleryMore = this.querySelector('[data-gallery-more]');
      if (!this.galleryMore) return;
      const button = this.galleryMore.querySelector('button');
      button.addEventListener('click', () => {
        this.gallery.classList.add('is-expanded');
        this.galleryMore.hidden = true;
      });
      this.applyGalleryLimit();
    }

    applyGalleryLimit() {
      if (!this.galleryMore || !this.galleryLimit) return;
      const items = this.visibleGalleryItems();
      items.forEach((item, index) => item.classList.toggle('is-beyond', index >= this.galleryLimit));
      const extra = items.length - this.galleryLimit;
      const count = this.galleryMore.querySelector('[data-gallery-more-count]');
      if (count) count.textContent = String(Math.max(extra, 0));
      this.galleryMore.hidden = extra <= 0 || this.gallery.classList.contains('is-expanded');
    }

    /*
     * Keeps the buying column in view while the photos scroll. When the
     * column is taller than the window it stops with its end in view.
     */
    setUpStickyInfo() {
      const info = this.querySelector('.product__info');
      if (!info || !('ResizeObserver' in window)) return;
      const update = () => {
        const header = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
        const room = window.innerHeight - info.offsetHeight - 112;
        info.style.setProperty('--info-top', `${Math.min(header + 16, room)}px`);
      };
      new ResizeObserver(update).observe(info);
      window.addEventListener('resize', update);
      update();
    }

    visibleGalleryItems() {
      return Array.from(this.gallery.querySelectorAll('.gallery__item:not([hidden])')).sort(
        (a, b) => Number(getComputedStyle(a).order) - Number(getComputedStyle(b).order)
      );
    }

    colorTokens(value) {
      const name = String(value).toLowerCase().trim();
      const code = name.split(/[：:\s\/]/)[0];
      return { name, code };
    }

    photoMatchesColor(alt, tokens) {
      if (!alt) return false;
      if (alt.includes(tokens.name)) return true;
      if (!tokens.code || tokens.code === tokens.name) return false;
      return alt.split(/[\s,、／\/]+/).some((word) => word === '#' + tokens.code || word === tokens.code);
    }

    applyColorPhotos(variant) {
      if (!this.gallery || !variant) return;
      const items = Array.from(this.gallery.querySelectorAll('.gallery__item'));
      const colorGroup = this.optionGroups.find((group) => group.hasAttribute('data-option-color'));

      if (colorGroup) {
        const index = Number(colorGroup.dataset.optionIndex);
        const allTokens = Array.from(colorGroup.querySelectorAll('[data-option-input]')).map((input) =>
          this.colorTokens(input.value)
        );
        const selected = this.colorTokens(variant.options[index]);
        const tagged = items.filter((item) =>
          allTokens.some((tokens) => this.photoMatchesColor(item.dataset.alt, tokens))
        );

        if (tagged.length) {
          tagged.forEach((item) => {
            item.hidden = !this.photoMatchesColor(item.dataset.alt, selected);
          });
        }
      }

      const mediaId = variant.featured_media ? String(variant.featured_media.id) : null;
      items.forEach((item) => item.classList.toggle('is-active', mediaId !== null && item.dataset.mediaId === mediaId));

      if (mediaId === null && !this.gallery.querySelector('.gallery__item.is-active:not([hidden])')) {
        const first = this.gallery.querySelector('.gallery__item:not([hidden])');
        if (first) first.classList.add('is-active');
      }

      this.gallery.scrollTo({ left: 0 });
      const total = this.querySelector('[data-gallery-total]');
      const current = this.querySelector('[data-gallery-current]');
      if (total) total.textContent = String(this.visibleGalleryItems().length);
      if (current) current.textContent = '1';
      this.applyGalleryLimit();
    }

    /* Sticky buy bar */

    setUpStickyBuy() {
      const bar = this.querySelector('[data-sticky-buy]');
      const form = this.querySelector('[data-product-form]');
      if (!bar || !form || !('IntersectionObserver' in window)) return;
      const mainButton = form.querySelector('[data-add-button]');

      const observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting;
        bar.hidden = !show;
        document.body.classList.toggle('has-sticky-buy', show);
      });
      observer.observe(mainButton);
    }

    /* Money */

    formatMoney(cents) {
      const format = this.moneyFormat.replace(/<[^>]*>/g, '');
      const withDelimiters = (number, precision, thousands, decimal) => {
        const fixed = (number / 100).toFixed(precision);
        const parts = fixed.split('.');
        const whole = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
        return parts[1] ? whole + decimal + parts[1] : whole;
      };

      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
        switch (key) {
          case 'amount':
            return withDelimiters(cents, 2, ',', '.');
          case 'amount_no_decimals':
            return withDelimiters(cents, 0, ',', '.');
          case 'amount_with_comma_separator':
            return withDelimiters(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withDelimiters(cents, 0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return withDelimiters(cents, 2, "'", '.');
          case 'amount_no_decimals_with_space_separator':
            return withDelimiters(cents, 0, ' ', ',');
          case 'amount_with_space_separator':
            return withDelimiters(cents, 2, ' ', ',');
          default:
            return withDelimiters(cents, 2, ',', '.');
        }
      });
    }
  }

  customElements.define('product-info', ProductInfo);
}

/*
 * Video and 3D model in the gallery. The preview picture is shown first;
 * the player or the viewer is created when the visitor asks for it, and
 * removed again when another one is started.
 */
if (!customElements.get('deferred-media')) {
  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      const button = this.querySelector('[data-media-play]');
      if (button) button.addEventListener('click', () => this.load());
    }

    load() {
      if (this.player) return;
      document.querySelectorAll('deferred-media').forEach((other) => {
        if (other !== this) other.unload();
      });

      const template = this.querySelector('template');
      if (!template || !template.content.firstElementChild) return;
      this.player = document.createElement('div');
      this.player.className = 'deferred-media__player';
      this.player.appendChild(template.content.firstElementChild.cloneNode(true));
      this.appendChild(this.player);
      this.classList.add('is-loaded');

      const model = this.player.querySelector('model-viewer');
      if (model && window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: (errors) => {
              if (!errors && this.player) this.viewer = new window.Shopify.ModelViewerUI(model);
            },
          },
        ]);
      }

      const focusable = this.player.querySelector('video, iframe, model-viewer');
      if (focusable) focusable.focus();
    }

    unload() {
      if (!this.player) return;
      this.player.remove();
      this.player = null;
      this.viewer = null;
      this.classList.remove('is-loaded');
    }
  }

  customElements.define('deferred-media', DeferredMedia);
}
